import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import { IconCirclePlus, IconMailQuestion } from '@rossum/ui/icons/tabler';
import { Button, Link, Stack, SvgIcon } from '@rossum/ui/material';
import { docsUrl } from '../../../../constants/config';
import { QUERY_KEY_QUEUE } from '../../../../features/queues/hooks/useQueue';
import { notify } from '../../../../features/toaster';
import { clearValidationMessagesAction } from '../../../../redux/modules/validationMessages/action';
import { InputMessages } from '../../../../redux/modules/validationMessages/types';
import CreateInboxModal from './CreateInboxModal';
import styles from './styles.module.sass';
import { useCreateInbox } from './useCreateInbox';

type Props = {
  clearValidationMessages: typeof clearValidationMessagesAction;
  validationMessages: InputMessages;
  selectedQueue: Queue;
};

const NoInboxPlaceholder = ({
  clearValidationMessages,
  validationMessages,
  selectedQueue,
}: Props) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<boolean>(false);

  const t = useIntl();

  const { mutate } = useCreateInbox(selectedQueue);

  const onCreate = ({ emailPrefix }: { emailPrefix: string }) =>
    mutate(emailPrefix, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_QUEUE, selectedQueue.id],
        });
        setOpen(false);
      },
      onError: () =>
        notify.error({
          title: t.formatMessage({
            id: 'containers.message.inbox.failed',
          }),
        }),
    });

  return (
    <>
      <Stack
        spacing={3}
        justifyContent="center"
        alignItems="center"
        sx={{ mt: 24 }}
      >
        <Stack spacing={1} justifyContent="center" alignItems="center">
          <SvgIcon fontSize="xLarge">
            <IconMailQuestion />
          </SvgIcon>
          <div>
            <FormattedMessage id="containers.settings.queues.email.noInbox" />
          </div>
          <div className={styles['Description']}>
            <FormattedMessage id="containers.settings.queues.email.noInbox.description" />
          </div>
          <Link
            href={`${docsUrl}/guides/import-and-export/#import-by-email`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div>
              <FormattedMessage id="containers.settings.queues.email.noInbox.moreInfo" />
            </div>
          </Link>
        </Stack>
        <Button
          variant="contained"
          startIcon={
            <SvgIcon fontSize="large">
              <IconCirclePlus />
            </SvgIcon>
          }
          onClick={() => setOpen(true)}
        >
          <FormattedMessage id="containers.settings.queues.email.noInbox.create" />
        </Button>
      </Stack>
      <CreateInboxModal
        clearValidationMessages={clearValidationMessages}
        isOpen={open}
        onCancel={() => setOpen(false)}
        onSubmit={onCreate}
        validationMessages={validationMessages}
      />
    </>
  );
};

export default NoInboxPlaceholder;
